import {
  GraphQLResponseWithoutData,
  Observable,
  RecordSource,
  Store,
  SubscribeFunction,
  TaskScheduler,
} from "relay-runtime";
import { config } from "../config";
import { refreshAuth } from "./Login/refreshAuth";
import {
  AuthSessionState,
  currentAuthSession,
  supportAccessEnd,
} from "./Login/State";
import { store } from "./Store";
import { createClient, CloseCode } from "graphql-ws";
import { Platform, unstable_batchedUpdates } from "react-native";
import { unstable_batchedUpdates as unstableBatchedUpdatesWeb } from "react-dom";
import { notifyClientStatus } from "./subPub";
import { SubscriptionStatus } from "./SubscriptionStatus";
import { addBreadcrumb } from "../common/sentry";
import Constants from "expo-constants";
import { sessionId } from "./sessionId";
import {
  createRelayEnvironmentWithMissingFieldHandlers,
  RelayNetwork,
} from "widgets";
import * as Sentry from "@sentry/react-native";
import { formatPayloadErrors } from "widgets/src/relayNetwork";
import { getAcceptLanguage } from "./getActiveLocale";

const getAuthSession = () => {
  const state = store.getState();
  return currentAuthSession(state);
};

let dispose: (() => void) | null;

export function RelayEnvironment(currentUser: string) {
  console.log("create relay environment");
  async function waitForSession(): Promise<AuthSessionState> {
    const currentSession = getAuthSession();

    if (currentSession?.user) {
      return currentSession;
    }
    return new Promise<AuthSessionState>((resolve) => {
      const unsubscribe = store.subscribe(() => {
        const s = getAuthSession();
        if (s?.user) {
          unsubscribe();
          resolve(s);
        }
      });
    });
  }

  let activeSocket: WebSocket | undefined;
  let timedOut: ReturnType<typeof setTimeout> | undefined;

  let subscriptionStatus = SubscriptionStatus.init;
  const setSubscriptionStatus = (status: SubscriptionStatus) => {
    console.log("subscription status", status);
    notifyClientStatus(status);
    subscriptionStatus = status;
  };

  if (dispose) {
    dispose();
    dispose = null;
  }
  const subscriptionsClient = createClient({
    lazyCloseTimeout: 20000,
    url: async () => {
      await waitForSession();
      return config.baseWSUrl + "/sub";
    },
    keepAlive: 10000,
    retryAttempts: 1000,
    connectionAckWaitTimeout: 3000,
    shouldRetry: (d) => {
      return true;
    },

    retryWait: async function randomisedExponentialBackoff(retries) {
      let retryDelay = 500;
      for (let i = 0; i < retries; i++) {
        retryDelay *= 2;
      }
      const retryIn = Math.min(
        retryDelay +
          (retries === 0 ? 0 : Math.floor(Math.random() * (500 - 50) + 200)),
        5000
      );

      await new Promise((resolve) => setTimeout(resolve, retryIn));
    },
    on: {
      connecting: () => {
        if (timedOut) {
          clearTimeout(timedOut);
        }
        setSubscriptionStatus(SubscriptionStatus.connecting);
      },
      connected: (socket: any) => {
        if (
          activeSocket &&
          subscriptionStatus !== SubscriptionStatus.refetching
        ) {
          setSubscriptionStatus(SubscriptionStatus.refetching);
        }
        setSubscriptionStatus(SubscriptionStatus.connected);
        activeSocket = socket;
      },
      closed: (c) => {
        const e = c as WebSocketCloseEvent;
        const session = getAuthSession();
        if (e.code === CloseCode.Forbidden) {
          if (session?.supportAccess) {
            store.dispatch(supportAccessEnd());
          } else if (session?.refreshToken) {
            refreshAuth();
          }
        }
        setSubscriptionStatus(SubscriptionStatus.closed);
      },
      ping: (received) => {
        if (!received && timedOut === undefined) {
          timedOut = setTimeout(() => {
            setSubscriptionStatus(SubscriptionStatus.timeout);
            subscriptionsClient.terminate();
          }, 5000);
        }
      },
      pong: (received) => {
        if (received && timedOut) {
          clearTimeout(timedOut);
          timedOut = undefined;
        }
      },
    },
    connectionParams: async () => {
      const session = await waitForSession();
      const locale = getAcceptLanguage();

      return {
        authToken: session.authToken,
        storeId: session.currentStoreId,
        language: locale,

        sessionId,
      };
    },
  });

  dispose = () => {
    subscriptionsClient.dispose();
  };

  const subFN: SubscribeFunction = (operation, variables) => {
    return Observable.create((sink) => {
      if (!operation.text) {
        return sink.error(new Error("Operation text cannot be empty"));
      }
      return subscriptionsClient.subscribe(
        {
          operationName: operation.name,
          query: operation.text,
          variables,
        },
        sink as any
      );
    });
  };

  const network = RelayNetwork.create(
    async (req) => {
      if (__DEV__) {
        console.log(
          "",
          req.params.name,
          JSON.stringify(req.variables, null, 2)
        );
      }
      const url = config.baseUrl + "/graphql";
      addBreadcrumb({
        type: "query",
        category: "gql",
        data: { operation: req.params.name, url, variables: req.variables },
      });

      const session = getAuthSession();

      return {
        url,
        headers: {
          "Accept-Language": getAcceptLanguage(),
          "x-storesprint-session-id": sessionId,
          "x-storesprint-store-id": session?.currentStoreId ?? "",
          "x-storesprint-client-name": Platform.OS,
          "x-storesprint-client-version": Constants.expoConfig?.version ?? "",
        },
      };
    },
    [
      (next) =>
        async function* (req) {
          try {
            for await (const res of next(req)) {
              const errors = (res as GraphQLResponseWithoutData).errors;

              const sentryEventId = (
                errors?.find((e: any) => e.extensions?.sentryEventId) as any
              )?.extensions?.sentryEventId;
              if (errors) {
                const errorCodes: string[] =
                  errors?.map((e) => (e as any)?.extensions?.code) ?? [];

                addBreadcrumb({
                  category: "gql",
                  message: "Error",
                  data: { errorCodes },
                });
                Sentry.withScope((scope) => {
                  scope.setTags({
                    errorCodes: errorCodes.join(","),
                    operator: req.params.id ?? req.params.name,
                    sentryEventId,
                    variables: JSON.stringify(req.variables),
                  });

                  const error = new Error(formatPayloadErrors(req, errors));

                  scope.captureException(error);
                });
              } else {
                addBreadcrumb({
                  category: "gql",
                  message: "Success",
                });
              }

              yield res;
            }
          } catch (e) {
            Sentry.addBreadcrumb({
              category: "gql",
              message: "Error",
              data: { e },
            });
            throw e;
          }
        },
      RelayNetwork.Middlewares.retry({
        timeout: 25000,
        retryDelays: [500, 2400, 4400],
        statusCodes: [500, 503, 504],
      }),
      RelayNetwork.Middlewares.auth({
        token: async () => {
          return (await waitForSession()).authToken;
        },
        refreshToken: async () => {
          const session = getAuthSession();

          // Support access tokens are non-refreshable — end the session on 401
          if (session?.supportAccess) {
            store.dispatch(supportAccessEnd());
            throw new Error("Support access session expired");
          }

          console.log("trying to refresh token");

          addBreadcrumb({
            category: "refreshToken",
            message: "Refreshing token",
            data: {
              expiredAt: session?.expiresAt,
            },
          });
          const res = await refreshAuth();
          const newToken = res?.authToken;
          if (newToken) {
            return newToken;
          } else {
            throw new Error("Failed to refresh token");
          }
        },
      }),
    ],
    subFN
  );

  const source = new RecordSource();
  const relayStore = new Store(source, {
    gcReleaseBufferSize: 10,
  });

  const RelayScheduler: TaskScheduler = {
    cancel: () => {},
    schedule: (task) => {
      if (Platform.OS === "web") {
        unstableBatchedUpdatesWeb(task);
      } else {
        unstable_batchedUpdates(task);
      }
      return "";
    },
  };

  const env = createRelayEnvironmentWithMissingFieldHandlers({
    network,
    store: relayStore,
    scheduler: RelayScheduler,
  });

  return env;
}
